import {MasterPolicyQuote} from 'Quoting/models/DTOs/quote';
import {A360ProductCd, A360ProductModel} from 'Quoting/models/Quoting';
import {toTitleCase} from 'Shared/utilities/stringFormatHelpers';
import {flatten, map} from 'fp-ts/lib/Array';
import {fold, fromNullable, getOrElse} from 'fp-ts/lib/Option';
import {pipe} from 'fp-ts/lib/pipeable';
import {List as IList, Map as IMap} from 'immutable';
import {getProductCdFromModel} from './helpers';
import {LocalStorageKey, fromSessionStorage} from 'Shared/utilities/localStorage';
import {map as IOmap} from 'fp-ts/lib/IO';
import {getFieldError, getValidationRuleset} from 'Quoting/components/QuoteForm/Plan/products/rules/rulesValidator';
import {generateRandomNumberFunc} from 'Shared/utilities/generateRandomNumberUtil';
import {QuoteStatus} from 'Quoting/models/enum';
import {Async, isLoaded} from 'Core/models/Async';
import {GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP} from 'Quoting/components/QuoteForm/Plan/PlanTemplates/GWL/gpluConstants';

export const VALUE_NOT_AVAILABLE = '-';

type PremiumMode = 'monthly' | 'annual';

export const MoneyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const formatPremium = (
  amount: number | undefined,
  mode?: PremiumMode,
  fallback: string = VALUE_NOT_AVAILABLE
) => {
  return pipe(
    amount,
    fromNullable,
    fold(
      () => fallback,
      amountValue => {
        const premium = MoneyFormatter.format(amountValue);
        return mode ? `${premium} /${getMode(mode)}` : premium;
      }
    )
  );
};

const getMode = (mode: PremiumMode) => {
  switch (mode) {
    case 'monthly':
      return 'mo*';

    case 'annual':
      return 'year';
  }
};

type RenderValueType = string | number | undefined | null;

export const safeRenderValue = (input: RenderValueType, titleCased?: boolean, fallback: string = VALUE_NOT_AVAILABLE) =>
  pipe(
    fromNullable(input),
    fold(
      () => fallback,
      value =>
        pipe(
          value,
          v => (typeof v === 'number' ? String(v) : v),
          v => v.replace(/null|undefined/g, ''), // remove null and undefined string from the value
          v => (titleCased ? toTitleCase(v).trim() : v),
          v => (/^[^a-zA-Z0-9]+$/.test(v) || v.length === 0 ? fallback : v) // if the value only contains special characters then return
        )
    )
  );

const quotePathRegex = /(\w+)\[(\d+)\]/;

export const parsePath = (path: string): (string | number)[] => {
  return pipe(
    path,
    fromNullable,
    fold(
      () => [],
      () =>
        pipe(
          path.split('.'),
          map(part => {
            const match = quotePathRegex.exec(part);
            if (match) {
              return [match[1], Number.parseInt(match[2], 10)];
            }
            return [part];
          }),
          flatten
        )
    )
  );
};

export const isDefined = <T>(value: T | null | undefined): value is NonNullable<T> =>
  value !== null && value !== undefined;

export const immutableMapToJS = (immutableObj: IMap<string, any>) =>
  isDefined(immutableObj) ? immutableObj.toJS() : null;

/**
 * Retrieves a value from an ImmutableMap object using a specified key path.
 * @param immutableMapObj The ImmutableMap object to retrieve the value from.
 * @param path The key path to follow to retrieve the value.
 * @returns The value found at the specified key path in the ImmutableMap object.
 */
export const getByKeyPath = (immutableMapObj: IMap<string, any>, path: string) =>
  IMap.isMap(immutableMapObj) ? immutableMapObj.getIn(parsePath(path)) : undefined;

export const setValueAtKeyPath = (immutableMapObj: IMap<string, any>, path: string, value: any) =>
  immutableMapObj.setIn(parsePath(path), value);

export function randomUUID() {
  // @ts-ignore
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    // @ts-ignore
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (generateRandomNumberFunc() * 16) | 0;
    const v = c == 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export const getKey = (
  rootId?: string,
  parentId?: string,
  withParentAndRootId: boolean = true
): {
  id: string;
  revisionNo: number;
  variation: string;
  rootId?: string;
  parentId?: string;
} => {
  const _key: any = {
    id: randomUUID(),
    revisionNo: 1,
    variation: 'quote',
  };

  if (withParentAndRootId) {
    _key['rootId'] = rootId ?? randomUUID();
    _key['parentId'] = parentId ?? randomUUID();
  }

  return _key;
};

export const createEisTypeObject = (entityName: string, rootId?: string, parentId?: string) => ({
  _type: entityName,
  _key: getKey(rootId, parentId),
});

export const getPercentageValue = (value: any, dataType: string) => {
  //if dataType is percentage then multiple by 100
  if (dataType === 'percentage' && typeof value === 'number') {
    return `${(value * 100).toFixed(2)}%`;
  }
  return value ?? '';
};

// function to retrieve benefit amount
export const getBenefitAmount = (
  defaultPlanValues: IMap<string, IMap<string, any>> | undefined,
  benefitPath: string
): number => {
  const defaultPlan = defaultPlanValues?.get('GAI7');
  const benefitAmountMap = defaultPlan?.get(benefitPath) as IMap<string, any>;
  const amount = benefitAmountMap?.get('amount');
  return amount !== undefined && !Number.isNaN(Number(amount)) ? Number(amount) : 0;
};

// function to retrieve benefit amount for GVA6
export const getBenefitAmountGVA6 = (
  defaultPlanValues: IMap<string, IMap<string, any>> | undefined,
  benefitPath: string
): number => {
  const defaultPlan = defaultPlanValues?.get('GVA6');
  const benefitAmountMap = defaultPlan?.get(benefitPath) as IMap<string, any>;
  const amount = benefitAmountMap?.get('amount');
  return amount !== undefined && !Number.isNaN(Number(amount)) ? Number(amount) : 0;
};

export const extractRootIdFromEntityUri = (uri: string) => {
  if (!uri) {
    throw new Error('Invalid entity uri');
  }
  return uri?.split('//')?.[2];
};

export const validateHealthProductQuote = (
  quote: MasterPolicyQuote | IMap<string, any>,
  forRating: boolean = false,
  isCompensationAdded?: boolean,
  useEISValidation?: boolean
): Record<string, string | undefined> => {
  if (useEISValidation) {
    return {};
  }

  if (!forRating && IMap.isMap(quote)) {
    return validateQuote(quote, Boolean(isCompensationAdded));
  }

  if (!IMap.isMap(quote)) {
    const productCd = getProductCdFromModel(quote._modelName as A360ProductModel);
    const payrollFrequencyField = [A360ProductCd.HI, A360ProductCd.CI].includes(productCd as A360ProductCd)
      ? 'payrollFrequency'
      : 'payrollFrequencyCd';
    const isPayrollFrequencySelected = quote?.benefitPackagingDetail?.plans.every(plan =>
      // @ts-ignore
      plan.coverageDefs.every(covDef => isDefined(covDef.fundingStructure[payrollFrequencyField]))
    );

    const isClassificationGroupNotAdded =
      quote?.classGroups.length === 0 ||
      quote.benefitPackagingDetail.plans.some(plan =>
        plan.coverageDefs.some(covDef => covDef.coverageGroupsLinks.length === 0)
      );

    const isPlanAdded = quote.benefitPackagingDetail.plans?.length > 0;

    // CI errors
    if (productCd === A360ProductCd.CI) {
      const isIndividualBenefitAmountError = quote.benefitPackagingDetail.plans?.every(plan =>
        plan.coverageDefs?.every(covDef =>
          isDefined(
            getFieldError(
              `${productCd}BenefitStructureIndividual.benefitAmount`,
              covDef.benefitStructures?.find(benefit => benefit?._type === 'CIBenefitStructureIndividual')?.benefitAmount.amount,
              getValidationRuleset(productCd)
            )
          )
        )
      );

      const isChildhoodConditionsError = quote.benefitPackagingDetail.plans?.every(plan =>
        plan.coverageDefs?.every(covDef =>
          isDefined(
            getFieldError(
              `${productCd}ChildhoodConditions.childhoodConditionsPct`,
              (covDef.benefitSchedule?.childhoodConditionsDetails?.childhoodConditionsPct ?? 0) * 100,
              getValidationRuleset(productCd)
            )
          )
        )
      );
      return getErrors(
        productCd,
        isPayrollFrequencySelected,
        isPlanAdded,
        isClassificationGroupNotAdded,
        isCompensationAdded,
        isIndividualBenefitAmountError,
        isChildhoodConditionsError
      );
    }

    return getErrors(
      productCd,
      isPayrollFrequencySelected,
      isPlanAdded,
      isClassificationGroupNotAdded,
      Boolean(isCompensationAdded)
    );
  }
  return {};
};

export const isNumericValue = (value: any) => /^-?\d+(\.\d+)?$/.test(value);

export const validateQuote = (
  quote: IMap<string, any>,
  isCompensationAdded: boolean
): Record<string, string | undefined> => {
  const productCd = getProductCdFromModel(quote.get('_modelName') as A360ProductModel);

  const payrollFrequencyField = [A360ProductCd.HI, A360ProductCd.CI].includes(productCd as A360ProductCd)
    ? 'payrollFrequency'
    : 'payrollFrequencyCd';

  const quotePlans = quote.getIn(parsePath('benefitPackagingDetail.plans')) as IList<any>;

  const isPayrollFrequencySelected = quotePlans?.every(plan =>
    (plan.get('coverageDefs') as IList<any>)?.every(covDef =>
      isDefined(covDef.get('fundingStructure').get(payrollFrequencyField))
    )
  );

  const isClassificationGroupNotAdded =
    quote?.get('classGroups')?.size === 0 ||
    quote
      ?.get('benefitPackagingDetails.plans')
      ?.some(plan => plan.get('coverageDefs')?.some(covDef => covDef.get('coverageGroupsLinks')?.size === 0));

  const isPlanAdded = quotePlans?.size > 0;

  //  CI errors
  if (productCd === A360ProductCd.CI) {
    const isIndividualBenefitAmountError = quotePlans?.every(plan =>
      (plan.get('coverageDefs') as IList<any>)?.every(covDef =>
        isDefined(
          getFieldError(
            `${productCd}BenefitStructureIndividual.benefitAmount`,
            covDef
              .get('benefitStructures')
              .get(0)
              .get('benefitAmount')
              .get('amount'),
            getValidationRuleset(productCd)
          )
        )
      )
    );

    const isChildhoodConditionsError = quotePlans?.every(plan =>
      (plan.get('coverageDefs') as IList<any>)?.every(covDef =>
        isDefined(
          getFieldError(
            `${productCd}ChildhoodConditions.childhoodConditionsPct`,
            (covDef
              .get('benefitSchedule')
              .get('childhoodConditionsDetails')
              .get('childhoodConditionsPct') ?? 0) * 100,
            getValidationRuleset(productCd)
          )
        )
      )
    );
    return getErrors(
      productCd,
      isPayrollFrequencySelected,
      isPlanAdded,
      isClassificationGroupNotAdded,
      isCompensationAdded,
      isIndividualBenefitAmountError,
      isChildhoodConditionsError
    );
  }

  return getErrors(
    productCd,
    isPayrollFrequencySelected,
    isPlanAdded,
    isClassificationGroupNotAdded,
    isCompensationAdded
  );
};

const getErrors = (
  productCd: string,
  isPayrollFrequencySelected: boolean,
  isPlanAdded: boolean,
  isClassificationGroupNotAdded: boolean,
  isCompensationAdded: boolean,
  isIndividualBenefitAmountError?: boolean,
  isChildhoodConditionsError?: boolean
) => ({
  [`${productCd}FundingStructure.payrollFrequency`]: isPayrollFrequencySelected
    ? undefined
    : "'Payroll Frequency' is mandatory.",
  [`${productCd}MasterPolicySummary.classGroups`]: isClassificationGroupNotAdded
    ? "At least one 'Classification Group' should be added to the quote."
    : undefined,
  [`${productCd}PolicyPackage.plans`]: isPlanAdded ? undefined : "At least one 'Plan' should be added to the quote.",
  compensation: isCompensationAdded
    ? undefined
    : 'Missing required attribute: Compensation is not added for the quote.',
  [`${productCd}BenefitStructureIndividual.benefitAmount`]: isIndividualBenefitAmountError
    ? "Value entered for 'Benefit Amount' is not allowed"
    : undefined,
  [`${productCd}ChildhoodConditions.childhoodConditionsPct`]: isChildhoodConditionsError
    ? 'Childhood conditions value is not in allowed range'
    : undefined,
});

export const getValueFromSessionStorage = (key: LocalStorageKey) => {
  const value = pipe(fromSessionStorage(key), IOmap(fromNullable))();
  return getOrElse(() => '')(value);
};

export const groupByKey = <T>(data: T[], key: string | number) =>
  data.reduce((group, current) => {
    return {
      ...group,
      [current[key]]: [...(group[current[key]] ?? []), current],
    };
  }, {});

export const isA360HealthProduct = (productModel: A360ProductModel) =>
  ['HIMaster', 'CIMaster', 'ACCMaster'].includes(productModel);

export const isA360LifeProduct = (productModel: A360ProductModel) => ['GPLUMaster'].includes(productModel);

export const isQuoteNotEditable = (quote: Async<IMap<string, any>>) =>
  isLoaded(quote) &&
  [
    QuoteStatus.ContractAccepted,
    QuoteStatus.CompanyDeclined,
    QuoteStatus.CustomerDeclined,
    QuoteStatus.Proposed,
    QuoteStatus.Sold,
    QuoteStatus.UWDeclined,
  ].includes(quote.Data.get('state'));

export const parseHelperText = (
  helperTextKey: string,
  helperTextValue: string | undefined,
  quote: Async<IMap<string, any>>
): string => {
  const cleanedHelperText = cleanHelperText(helperTextValue);

  const parts = helperTextKey.split('__');

  const planKey = parts.length > 0 ? parts[1] : null;

  if (isLoaded(quote) && planKey) {
    const plans = getByKeyPath(quote?.Data, 'benefitPackagingDetail.plans') as IList<any>;
    if (plans?.size > 0) {
      const plan = plans?.find(plan => getByKeyPath(plan, '_key.id') === planKey) as IMap<string, any>;
      const planName = getByKeyPath(plan, 'planName') as string;
      if (planName) {
        return `${planName}: ${cleanedHelperText}`;
      }
    }
  }
  return cleanedHelperText;
};

export const cleanHelperText = (helperText: string | undefined) =>
  typeof helperText === 'string' ? helperText?.replace(/:minMax/gi, '') : undefined;

export const createUniqueFormFieldId = (formFieldId: string, planKey: string) => {
  if (!formFieldId) {
    return undefined;
  }

  if (formFieldId && formFieldId?.startsWith('plan') && planKey) {
    return formFieldId?.concat(`__${planKey}`);
  }

  return formFieldId;
};
export const getCensusClassURI = (censusClassRootId: string) =>
  `geroot://CensusClassBase/CensusClass//${censusClassRootId}`;

export const getOpportunityURI = (opportunityRootId: string) =>
  `geroot://Opportunity/Opportunity//${opportunityRootId}`;

export const getCoverageNameByCoverageCode = (productModel: A360ProductModel, coverageCd: string) => {
  if (productModel === A360ProductModel.GPLUMaster) {
    return GPLU_COVERAGE_CODE_TO_COVERAGE_NAME_MAP[coverageCd];
  }

  return VALUE_NOT_AVAILABLE;
};
